/**
 * **Legacy Tableau** — the frozen dashboards, embedded. `APPLICATION_SPEC.md` §5.4.
 *
 * Four screens, read out of the legacy portal's own BigQuery table on 8 Sep:
 * Story, Writer, Revenue, Channel. Embedding (rather than linking) is Nilay's
 * call, confirmed 8 Sep; it departs from SOW §02 and Dishant's 3 Sep Slack
 * answer, so **Q-I1 stays open as a contractual sign-off.**
 *
 * ⚠️ **Never claim embedding removes the Tableau seat requirement.** The
 * Connected App JWT impersonates one licensed identity — `dishant@dataceva.com`
 * — so viewers need no seat of their own, but *that* seat must stay licensed.
 *
 * **The frozen-data banner was removed on 9 Sep** (Nilay) to stop it taking a
 * line off dashboards that are already taller than the viewport. Two facts went
 * with it, and here is where each now lives:
 *
 * · *"frozen"* survives **per view, in the card footer**, as `Frozen On
 *   <date>` — better placed, next to the workbook it describes rather than
 *   above all four. The trailing *"This view will never refresh."* went on
 *   17 Sep (Nilay): the freeze date is the fact, and the sentence restated it.
 * · *"viewing still needs a licensed Tableau identity"* is now **only in the
 *   docs** (spec §5.4, Q-I1). The standing rule is not to *claim* the seat
 *   requirement is removed, and saying nothing is not a claim — but it is worth
 *   knowing the app no longer states it anywhere.
 *
 * The server still returns `banner`, so restoring it is a one-line change.
 *
 * **The Revenue tab is Admin-only** (RV-3; gated on `revenue_view`). Measured:
 * the old portal granted the other three views to 14 of 15 users and Revenue to
 * 3. The tab list is filtered server-side; the token endpoint is the actual
 * control.
 *
 * **The per-screen `note` the API returns is deliberately not rendered** (Nilay,
 * 9 Sep). These dashboards are 1080px tall and fixed-width; a sentence
 * describing the one you are looking at costs a line of the dashboard itself.
 * The field stays on the API because it documents what each screen was for,
 * which is worth having in `screens.py` and in the OpenAPI schema.
 *
 * The embed is built imperatively rather than as JSX. `<tableau-viz>` is a
 * custom element from Tableau's own script, and handing React a foreign element
 * whose attributes must be set before it upgrades is how you get a blank frame.
 */
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { Tabs } from '../components/Tabs'
import { Button, Card, Note, Skeleton } from '../components/ui'
import { api, ApiError, type LegacyScreen } from '../lib/api'
import { ViewHeader } from '../shell/ViewHeader'
import { formatDate } from '../lib/dates'

/** Tableau's Embedding API v3, served by the site we embed from. */
const EMBED_SCRIPT_PATH = '/javascripts/api/tableau.embedding.3.latest.min.js'

let scriptPromise: Promise<void> | null = null

/**
 * Load Tableau's embedding script once per session.
 *
 * Derived from the token's own `src` rather than configured here: the script has
 * to come from the same Tableau site the view does, and two sources of truth for
 * the host is how a working token ends up pointed at the wrong server.
 */
function loadEmbedScript(src: string): Promise<void> {
  if (scriptPromise) return scriptPromise
  const origin = new URL(src).origin
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${origin}${EMBED_SCRIPT_PATH}"]`,
    )
    if (existing) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.type = 'module'
    script.src = `${origin}${EMBED_SCRIPT_PATH}`
    script.onload = () => resolve()
    script.onerror = () =>
      reject(new Error(`Could Not Load Tableau's Embedding Script from ${origin}`))
    document.head.appendChild(script)
  })
  return scriptPromise
}

/**
 * Turn a `vizloaderror` into something a human can act on.
 *
 * The event's `detail` is an object, and `String(detail)` renders the literal
 * text `[object Object]` — which is what this page showed on its first run.
 * Tableau's numeric codes are the useful part, and **10084 is the one this
 * project actually hits**: measured on 9 Sep against the live site, the
 * Connected App's `kid` is recognised (a wrong one answers 10085) but the
 * sign-in is still refused, which narrows it to a rotated secret or an
 * impersonated user who no longer holds a seat. Neither is fixable from here,
 * so the message says who can fix it.
 */
function describeVizError(event: CustomEvent): string {
  const detail = event.detail as
    | { errorCode?: string | number; message?: string; errorMessage?: unknown }
    | string
    | undefined

  if (typeof detail === 'string' && detail.trim()) return detail

  const code = String(
    (detail as { errorCode?: string | number } | undefined)?.errorCode ?? '',
  )
  const message =
    (detail as { message?: string } | undefined)?.message ??
    (typeof (detail as { errorMessage?: unknown } | undefined)?.errorMessage === 'string'
      ? String((detail as { errorMessage?: unknown }).errorMessage)
      : '')

  if (code.includes('10084') || message.includes('10084')) {
    return (
      'Tableau Refused the Embed Token (Error 10084). The Connected App Itself Is ' +
      'Recognised, so This Is Either a Rotated Secret or an Impersonated Account ' +
      'That No Longer Holds a Tableau Seat. Both Need Someone with Access to the ' +
      "Client's Tableau Site Admin."
    )
  }
  if (code || message) {
    return `Tableau Refused to Render This View${code ? ` (error ${code})` : ''}. ${message}`.trim()
  }
  return 'Tableau Refused to Render This View, and Reported No Reason.'
}

/**
 * How tall to draw the viz.
 *
 * Fullscreen gets the viewport minus the control row; otherwise 1080, which is
 * what the portal served and what these dashboards were authored at. The floor
 * stops a short window producing a frame too small to read.
 */
function vizHeight(): string {
  return document.fullscreenElement
    ? String(Math.max(window.innerHeight - 56, 480))
    : '1080'
}

interface FullscreenControl {
  isFullscreen: boolean
  toggle: () => void
}

function Embed({
  screen,
  onFullscreenChange,
}: {
  screen: LegacyScreen
  /** Lets the Card render the control on its header line — LG-1. */
  onFullscreenChange?: (control: FullscreenControl) => void
}) {
  const container = useRef<HTMLDivElement>(null)
  const wrapper = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const toggleFullscreen = useCallback(() => {
    const node = wrapper.current
    if (!node) return
    if (document.fullscreenElement) void document.exitFullscreen()
    else void node.requestFullscreen?.()
  }, [])

  // Driven by the event rather than by the click, so pressing Escape — which
  // the browser handles itself and never tells the click handler about — still
  // puts the button back to "Full screen".
  // The Card owns the button; this owns the wrapper the API acts on. Reported
  // upward on every change so the label cannot drift from the real state — an
  // Escape keypress exits fullscreen without going through our handler.
  useEffect(() => {
    onFullscreenChange?.({ isFullscreen, toggle: toggleFullscreen })
  }, [isFullscreen, onFullscreenChange, toggleFullscreen])

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === wrapper.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  /**
   * 🔴 **Resize in place, never re-mount.**
   *
   * The first version had `isFullscreen` in the mount effect's dependencies, so
   * pressing Full screen tore the viz down and rebuilt it — a spinner and a
   * fresh load from Tableau every time, for a change of height. The mount
   * effect now depends on the token and the screen only, and this one sets the
   * attribute on the element that is already there.
   */
  useEffect(() => {
    const viz = container.current?.querySelector('tableau-viz')
    viz?.setAttribute('height', vizHeight())
  }, [isFullscreen])

  // Keyed on the screen so switching tabs mints a fresh token rather than
  // re-pointing a live viz — Tableau rejects a replayed `jti`, so a cached
  // token would fail on the second tab a reader opens.
  const token = useQuery({
    queryKey: ['legacy-token', screen.key],
    queryFn: () => api.legacyToken(screen.key),
    // Re-mint before the 300s life runs out. A stale token renders an empty
    // frame with nothing in the console to explain it.
    refetchInterval: (query) =>
      query.state.data ? Math.max(query.state.data.expires_in_seconds - 30, 30) * 1000 : false,
    retry: false,
  })

  useEffect(() => {
    const data = token.data
    const node = container.current
    if (!data || !node) return

    let cancelled = false
    setError(null)

    loadEmbedScript(data.src)
      .then(() => {
        if (cancelled || !container.current) return
        const host = container.current
        host.replaceChildren()
        const viz = document.createElement('tableau-viz')
        viz.setAttribute('id', `tableau-${screen.key}`)
        viz.setAttribute('src', data.src)
        viz.setAttribute('token', data.token)
        viz.setAttribute('width', '100%')
        // 1080 is the portal's own figure and what these dashboards were
        // authored at. Fullscreen changes it in place — see below.
        viz.setAttribute('height', vizHeight())
        viz.setAttribute('toolbar', 'bottom')
        // The data is frozen, so there is nothing for a device-size tab bar to
        // reflow between; a fixed height matches what the old portal served.
        viz.setAttribute('hide-tabs', 'false')
        viz.addEventListener('vizloaderror', (event) => {
          if (cancelled) return
          setError(describeVizError(event as CustomEvent))
          // Drop the dead frame. `<tableau-viz>` keeps its declared height after
          // a failed load, which left ~1080px of blank card under the error
          // message — the page read as broken rather than as explained.
          host.replaceChildren()
        })
        host.appendChild(viz)
      })
      .catch((loadError: Error) => {
        if (!cancelled) setError(loadError.message)
      })

    return () => {
      cancelled = true
      node.replaceChildren()
    }
  }, [token.data, screen.key])

  if (token.isLoading) return <Skeleton rows={6} />

  if (token.error) {
    const status = token.error instanceof ApiError ? token.error.status : 0
    return (
      <Note tone="error">
        {status === 403
          ? 'You Do Not Have Permission to Open This Dashboard.'
          : status === 503
            ? 'This App Has Not Been Given Tableau Connected App Credentials, so It Cannot Mint an Embed Token. Nothing Is Wrong with the Dashboard Itself.'
            : 'Could Not Obtain an Embed Token.'}
      </Note>
    )
  }

  return (
    <div ref={wrapper} style={{ position: 'relative', background: 'var(--bg-surface)' }}>
      {error && <Note tone="error">{error}</Note>}

      {/* **Full screen, because these dashboards are fixed-width.** They were
          authored at a desktop size the app's content column cannot match once
          the sidebar takes its 236px, so the frame scrolls sideways. Tableau's
          own toolbar has a full-screen item, but it is a small icon in a busy
          bar; this is the same capability where somebody will look for it.
          Uses the browser Fullscreen API on the wrapper, so it works whatever
          the toolbar is doing.

          🔴 **LG-1, 10 Sep: the button no longer lives here.** It had a
          full-width right-aligned row of its own directly above a 1080px frame,
          which spent a whole band of the page on one control. It is now reported
          upward through `onFullscreenChange` and rendered in the Card's `action`
          slot, on the header line that already exists. The state stays here
          because the Fullscreen API acts on *this* wrapper. */}

      {/* No loading line and no reserved height. A dashboard that takes a
          moment is obvious from the empty frame, and an empty 1080px card with
          a sentence in it read as a broken page. */}
      <div ref={container} style={{ minHeight: error ? 0 : undefined }} />
    </div>
  )
}

export function Legacy() {
  const screens = useQuery({ queryKey: ['legacy-screens'], queryFn: api.legacyScreens })
  const [active, setActive] = useState<string | null>(null)
  // Reported up by `Embed`, which owns the element the Fullscreen API acts on.
  const [fullscreen, setFullscreen] = useState<FullscreenControl | null>(null)

  if (screens.isLoading) return <Skeleton rows={6} />
  if (screens.error || !screens.data) {
    return <Note tone="error">Could Not Load the Legacy Dashboards.</Note>
  }

  const data = screens.data
  const current = data.screens.find((screen) => screen.key === active) ?? data.screens[0]
  const withheld = data.total_screens - data.screens.length

  return (
    <div>
      <ViewHeader
        title="Frozen Tableau Dashboards"
        status={`${data.screens.length} of ${data.total_screens} Dashboards · Data Frozen ${formatDate(data.frozen_on)}`}
        scoped={false}
      />

      {!data.configured && (
        <Note tone="error">
          Tableau Connected App Credentials Are Not Configured in This Environment, so No
          Embed Token Can Be Minted. The Dashboards Below Are Listed for Reference Only.
        </Note>
      )}

      {withheld > 0 && (
        <Note>
          {withheld === 1 ? 'One Dashboard Is' : `${withheld} dashboards are`} not shown
          because {withheld === 1 ? 'it needs' : 'they need'} the revenue permission.
        </Note>
      )}

      {data.screens.length === 0 ? (
        <Note>No Legacy Dashboards Are Available to You.</Note>
      ) : (
        <>
          <Tabs
            tabs={data.screens.map((screen) => ({ key: screen.key, label: screen.title }))}
            active={current!.key}
            onChange={setActive}
            label="Legacy dashboard"
          />

          {/* **No card title and no per-screen blurb.** The tab above already
              says which dashboard this is, and a paragraph describing a
              dashboard the reader is looking at is words in the way — the
              dashboards are 1080px tall and every line above them is a line of
              the thing itself pushed off screen. The workbook and view stay in
              the footer, where they are provenance rather than preamble. */}
          <Card
            // LG-1: the header line already exists, so the control costs no
            // vertical space here. It was a full-width row of its own above a
            // 1080px frame.
            action={
              fullscreen && (
                <Button variant="secondary" onClick={fullscreen.toggle}>
                  {fullscreen.isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
                </Button>
              )
            }
            footer={
              <span>
                {/* **`This View Will Never Refresh.` was dropped 17 Sep** (Nilay).
                    `Frozen On <date>` already says it — frozen is the claim, and
                    the sentence after it was the same fact restated for emphasis
                    on a screen whose whole job is showing the dashboard. */}
                {/* **One font — `is-mono` dropped 17 Sep** (Nilay: *"can this be
                    in same font? right now its looking odd"*). It was two
                    changes at once, not one: `.is-mono` swaps the family **and**
                    the size (0.8125rem against the footer's own scale), so two
                    words in the middle of a sentence sat taller than the text
                    around them. It was also against our own rule —
                    DESIGN-SYSTEM.md reserves mono for **asset ids and request
                    ids**, machine strings you compare character by character. A
                    workbook and a view are names you read. */}
                Workbook {current!.workbook} · View {current!.view}. Frozen On{' '}
                {formatDate(data.frozen_on)}.
              </span>
            }
          >
            <Embed screen={current!} onFullscreenChange={setFullscreen} />
          </Card>
        </>
      )}

      <p className="mt-4 mb-0 text-sm">
        <Link to="/connections" style={{ color: 'var(--brand-ink)' }}>
          Back to Connections
        </Link>
      </p>
    </div>
  )
}
